import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown, ArrowRight, BellOff, BellRing, Bot, CalendarCheck, CalendarSearch, CalendarX,
  CheckCircle2, ClipboardList, Clock4, FileClock, Gauge, GraduationCap, Headset, MessageSquareX,
  Moon, PhoneCall, PhoneForwarded, PhoneIncoming, PhoneMissed, ShieldAlert, ShieldCheck, Sun,
  UserPlus, Users, Workflow,
} from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { Reveal, SectionHeading, EASE } from "../components/site/Reveal";
import { DentalLeadForm } from "../components/site/DentalLeadForm";
import {
  DENTAL_COVERAGE, DENTAL_DURING_HOURS, DENTAL_FAQS, DENTAL_PROBLEMS, DENTAL_SERVICES, DENTAL_STEPS, DENTAL_WHY,
} from "../data/dental";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSite } from "../context/SiteContext";

const ICONS = {
  PhoneMissed, ShieldAlert, CalendarX, BellOff, FileClock, MessageSquareX, PhoneCall, CalendarCheck,
  CalendarSearch, ShieldCheck, BellRing, ClipboardList, UserPlus, CheckCircle2, PhoneForwarded,
  Headset, Clock4, GraduationCap, Workflow, Bot, Users, Gauge,
};

const FlowStep = ({ children, accent }) => (
  <div className={`rounded-xl px-5 py-3.5 text-sm font-semibold text-center ${accent ? "bg-brand-navy text-white dark:bg-brand-sky dark:text-brand-ink" : "bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200"}`}>
    {children}
  </div>
);

const FlowArrow = () => <ArrowDown size={16} className="mx-auto text-brand-sky" />;

const Dental = () => {
  const { scrollToId } = useSite();

  useEffect(() => {
    document.title = "Dental Virtual Front Desk & 24/7 Call Support for Dental Practices | Medlyn 360";
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content",
      "24/7 call answering and trained Dental Virtual Assistants for US dental practices — appointment scheduling, insurance verification, recall outreach and missed-call recovery that work alongside your existing front desk. Keep your phone number. Book a free consultation.");
    return () => {
      document.title = "Medlyn 360 — Virtual Live Human Medical Scribing";
      if (prev) meta?.setAttribute("content", prev);
    };
  }, []);

  return (
    <main data-testid="dental-page">
      <Navbar />

      {/* Hero */}
      <section id="dental-hero" className="relative overflow-hidden bg-gradient-to-br from-white via-brand-sky-soft/60 to-white dark:from-brand-ink dark:via-[#0A1030] dark:to-brand-ink pt-32 pb-20 md:pt-44 md:pb-28 grain">
        <div className="absolute -top-24 right-0 w-[480px] h-[480px] rounded-full bg-brand-sky/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div>
            <Reveal y={16}>
              <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-sky animate-pulse" />
                <span className="text-xs font-bold tracking-[0.18em] uppercase text-brand-navy dark:text-brand-sky">Dental Virtual Front Desk</span>
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-7 font-display font-bold tracking-tighter leading-[1.06] text-slate-900 dark:text-white text-4xl sm:text-5xl lg:text-6xl" data-testid="dental-hero-headline">
                Your Front Desk <span className="text-brand-gradient">Doesn't Have to Do It All.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                24/7 call support and trained Dental Virtual Assistants who work alongside your existing team to help
                answer calls, manage appointments, verify insurance and keep patients from falling through the cracks.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  data-testid="dental-hero-consultation-button"
                  onClick={() => scrollToId("dental-contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
                >
                  Book a Free Consultation <ArrowRight size={15} />
                </button>
                <button
                  data-testid="dental-hero-pilot-button"
                  onClick={() => scrollToId("dental-contact")}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 dark:border-white/15 text-slate-700 dark:text-slate-200 font-bold px-8 py-4 text-sm hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
                >
                  Explore a Pilot
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-7 flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="dental-reassurance-line">
                <CheckCircle2 size={14} className="text-brand-sky shrink-0" />
                Keep your existing phone number. Keep your existing front desk. Medlyn works alongside your team.
              </p>
            </Reveal>
          </div>

          {/* Call-support mock */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="relative hidden md:block"
            data-testid="dental-hero-illustration"
          >
            <div className="absolute -inset-8 rounded-[2.5rem] bg-brand-sky/10 blur-2xl" />
            <div className="relative rounded-3xl bg-white/90 dark:bg-white/[0.06] border border-slate-100 dark:border-white/10 shadow-[0_40px_90px_-40px_rgba(42,49,146,0.45)] p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Front Desk — Line 2</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE</span>
              </div>
              <div className="mt-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-navy dark:bg-brand-sky flex items-center justify-center shrink-0">
                  <PhoneIncoming size={16} className="text-white dark:text-brand-ink" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white">Incoming — Sarah M. (recall due)</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Answered by Medlyn Dental VA · 00:07</p>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <div className="rounded-xl bg-brand-sky/10 border border-brand-sky/25 px-4 py-3">
                  <p className="text-xs font-bold text-brand-navy dark:text-brand-sky">Appointment booked — Tue 9:40 AM</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Hygiene recall · Dr. Patel · Operatory 3</p>
                </div>
                <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 px-4 py-3 flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Insurance eligibility verified</p>
                  <CheckCircle2 size={14} className="text-emerald-500" />
                </div>
                <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 px-4 py-3 flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Confirmation text scheduled</p>
                  <CheckCircle2 size={14} className="text-emerald-500" />
                </div>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1, duration: 0.45, ease: EASE }} className="absolute -left-8 top-6 animate-float-slow">
              <div className="rounded-full bg-white/95 dark:bg-[#0D1434]/95 border border-white/60 dark:border-white/10 pl-2.5 pr-4 py-2 flex items-center gap-2 shadow-lg shadow-brand-navy/10">
                <span className="w-7 h-7 rounded-full bg-brand-navy dark:bg-brand-sky flex items-center justify-center"><Clock4 size={13} className="text-white dark:text-brand-ink" /></span>
                <span className="text-xs font-bold text-slate-800 dark:text-white">24/7 Call Coverage</span>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.15, duration: 0.45, ease: EASE }} className="absolute -right-4 bottom-8 animate-float-slow" style={{ animationDelay: "1.5s" }}>
              <div className="rounded-full bg-white/95 dark:bg-[#0D1434]/95 border border-white/60 dark:border-white/10 pl-2.5 pr-4 py-2 flex items-center gap-2 shadow-lg shadow-brand-navy/10">
                <span className="w-7 h-7 rounded-full bg-brand-navy dark:bg-brand-sky flex items-center justify-center"><Users size={13} className="text-white dark:text-brand-ink" /></span>
                <span className="text-xs font-bold text-slate-800 dark:text-white">Works With Your Team</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section id="dental-problem" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="dental-problem-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            id="dental-problem"
            overline="The Problem"
            title={<>Every missed call could be <span className="text-brand-gradient">a missed patient.</span></>}
            sub="Dental front desks are constantly balancing ringing phones, patients at the desk, insurance verification, scheduling, cancellations, recalls and treatment follow-ups. When the team gets overwhelmed:"
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DENTAL_PROBLEMS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.text} delay={(i % 3) * 0.07}>
                  <div className="h-full rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-6 flex items-center gap-4" data-testid={`dental-problem-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-brand-navy/5 dark:bg-brand-sky/10 border border-brand-navy/10 dark:border-brand-sky/20 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-brand-navy dark:text-brand-sky" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
              Medlyn provides an additional layer of support so important front-desk work doesn't fall through the cracks.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl font-display text-2xl md:text-3xl font-semibold tracking-tight text-brand-navy dark:text-brand-sky leading-snug border-l-4 border-brand-sky pl-6" data-testid="dental-roi-pullquote">
              What if one recovered patient paid for your entire month of front-desk support?
            </p>
          </Reveal>
        </div>
      </section>

      {/* 24/7 Call Support */}
      <section id="dental-call-support" className="bg-slate-50 dark:bg-[#0A1030] py-24 md:py-32" data-testid="dental-call-support-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div className="lg:sticky lg:top-32">
            <SectionHeading
              id="dental-247"
              overline="24/7 Call Support"
              title={<>Your practice may close. <span className="text-brand-gradient">Patient calls don't.</span></>}
              sub="Medlyn helps dental practices provide continuous call coverage without requiring the existing front desk to be available around the clock."
            />
          </div>
          <div className="space-y-6">
            <Reveal>
              <div className="rounded-[2rem] bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8 md:p-10">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-6 flex items-center gap-2"><Sun size={14} /> During practice hours, Medlyn can support</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                  {DENTAL_DURING_HOURS.map((item) => (
                    <p key={item} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300">
                      <CheckCircle2 size={15} className="text-brand-sky shrink-0 mt-0.5" /> {item}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand-navy to-brand-navy-deep p-8 md:p-10 grain">
                <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-sky/20 blur-3xl" />
                <p className="relative text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-4 flex items-center gap-2"><Moon size={14} /> Outside normal practice hours</p>
                <p className="relative text-sm md:text-base leading-relaxed text-white/75">
                  Calls can be handled through Medlyn's AI-assisted call support according to practice-approved
                  workflows, with appropriate escalation when human attention is required.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VA Services */}
      <section id="dental-services" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="dental-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            id="dental-va"
            overline="Dental Virtual Assistant Services"
            title={<>More than <span className="text-brand-gradient">an answering service.</span></>}
            sub="Medlyn Dental VAs can support the full range of front-desk workflows — trained on your systems, your rules and your patient-experience standards."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DENTAL_SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={(i % 4) * 0.06}>
                  <div className="h-full rounded-3xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(42,49,146,0.35)] transition-[transform,box-shadow] duration-300" data-testid={`dental-service-card-${i}`}>
                    <div className="w-12 h-12 rounded-2xl bg-brand-navy dark:bg-brand-sky flex items-center justify-center">
                      <Icon size={20} className="text-white dark:text-brand-ink" />
                    </div>
                    <h3 className="mt-5 font-display text-base font-semibold text-slate-900 dark:text-white leading-snug">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="dental-how" className="bg-slate-50 dark:bg-[#0A1030] py-24 md:py-32" data-testid="dental-how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            id="dental-how"
            overline="How It Works"
            title={<>We work alongside <span className="text-brand-gradient">your existing front desk.</span></>}
          />
          <div className="mt-14 grid md:grid-cols-5 gap-5">
            {DENTAL_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7" data-testid={`dental-step-${i + 1}`}>
                  <span className="font-display text-3xl font-bold text-brand-sky/40">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-900 dark:text-white">{s.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section id="dental-coverage" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="dental-coverage-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            id="dental-coverage"
            overline="Choose Your Coverage"
            title={<>Support that fits <span className="text-brand-gradient">how your practice runs.</span></>}
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
            {DENTAL_COVERAGE.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <div
                    className={`h-full flex flex-col rounded-[2rem] p-9 ${
                      c.featured
                        ? "relative overflow-hidden bg-gradient-to-br from-brand-navy to-brand-navy-deep text-white shadow-[0_40px_90px_-40px_rgba(42,49,146,0.7)]"
                        : "bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10"
                    }`}
                    data-testid={`dental-coverage-card-${i}`}
                  >
                    {c.featured && <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-brand-sky/20 blur-3xl" />}
                    <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center ${c.featured ? "bg-white/10 border border-white/15" : "bg-brand-navy dark:bg-brand-sky"}`}>
                      <Icon size={20} className={c.featured ? "text-brand-sky" : "text-white dark:text-brand-ink"} />
                    </div>
                    <h3 className={`relative mt-6 font-display text-xl font-semibold ${c.featured ? "text-white" : "text-slate-900 dark:text-white"}`}>{c.title}</h3>
                    <p className={`relative mt-3 text-sm leading-relaxed ${c.featured ? "text-white/70" : "text-slate-600 dark:text-slate-300"}`}>{c.desc}</p>
                    <p className={`relative mt-4 flex-1 text-[13px] leading-relaxed font-medium ${c.featured ? "text-brand-sky" : "text-slate-400"}`}>{c.best}</p>
                    <button
                      data-testid={`dental-pricing-button-${i}`}
                      onClick={() => scrollToId("dental-contact")}
                      className={`relative mt-7 rounded-full font-bold px-6 py-3 text-sm transition-shadow duration-300 ${
                        c.featured
                          ? "bg-white text-brand-navy hover:shadow-[0_16px_40px_-14px_rgba(255,255,255,0.4)]"
                          : "bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)]"
                      }`}
                    >
                      Get Custom Pricing
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Medlyn */}
      <section id="dental-why" className="bg-slate-50 dark:bg-[#0A1030] py-24 md:py-32" data-testid="dental-why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <SectionHeading
            id="dental-why"
            overline="Why Medlyn 360?"
            title={<>Built by <span className="text-brand-gradient">healthcare professionals.</span></>}
            sub="Medlyn isn't a generic virtual assistant company. Our workflows are designed with an understanding of healthcare practices and the importance of patient experience, accuracy, privacy and reliable administrative support."
          />
          <div className="grid sm:grid-cols-2 gap-4">
            {DENTAL_WHY.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <Reveal key={w.text} delay={(i % 2) * 0.06}>
                  <div className="rounded-2xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-5 flex items-center gap-4" data-testid={`dental-why-item-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-brand-sky/10 border border-brand-sky/25 flex items-center justify-center shrink-0">
                      <Icon size={17} className="text-brand-navy dark:text-brand-sky" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Keep Your Existing Setup */}
      <section id="dental-setup" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="dental-setup-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            id="dental-setup"
            overline="Keep Your Existing Setup"
            title={<>No need to change the number <span className="text-brand-gradient">your patients already know.</span></>}
            sub="Medlyn aims to work with your practice's existing phone setup wherever possible — call routing is configured around the coverage model you choose."
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Reveal>
              <div className="rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8" data-testid="dental-flow-day">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-6 flex items-center gap-2"><Sun size={14} /> During Practice Hours</p>
                <div className="space-y-2.5">
                  <FlowStep>Patient Calls Practice</FlowStep>
                  <FlowArrow />
                  <FlowStep>Existing Practice Number</FlowStep>
                  <FlowArrow />
                  <FlowStep accent>Front Desk / Medlyn Dental VA</FlowStep>
                  <FlowArrow />
                  <FlowStep>Scheduling • Insurance • Follow-Up • Escalation</FlowStep>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8" data-testid="dental-flow-night">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-6 flex items-center gap-2"><Moon size={14} /> After Hours</p>
                <div className="space-y-2.5">
                  <FlowStep>Existing Practice Number</FlowStep>
                  <FlowArrow />
                  <FlowStep accent>24/7 AI-Assisted Call Support</FlowStep>
                  <FlowArrow />
                  <FlowStep>Scheduling • Information • Message • Escalation</FlowStep>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Dental FAQ */}
      <section id="dental-faq" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="dental-faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16">
          <div className="lg:sticky lg:top-32 self-start">
            <SectionHeading
              id="dental-faq"
              overline="Dental FAQ"
              title={<>Questions dental offices <span className="text-brand-gradient">ask us most</span></>}
              sub="Systems, scripts, pilots, privacy and pricing — how the Dental Virtual Front Desk actually works day to day."
            />
            <Reveal delay={0.12}>
              <Link
                to="/resources"
                data-testid="dental-resources-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-navy dark:text-brand-sky hover:underline underline-offset-4"
              >
                Read our dental front-desk guides <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="space-y-4" data-testid="dental-faq-accordion">
              {DENTAL_FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`dental-faq-${i}`}
                  className="rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 px-6 data-[state=open]:shadow-[0_20px_50px_-30px_rgba(42,49,146,0.35)] transition-shadow duration-300"
                  data-testid={`dental-faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="text-left font-display text-base md:text-lg font-semibold text-slate-900 dark:text-white hover:no-underline py-5"
                    data-testid={`dental-faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-[15px] leading-relaxed text-slate-600 dark:text-slate-300 pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Final CTA + Lead form */}
      <section id="dental-contact" className="bg-slate-50 dark:bg-[#0A1030] py-24 md:py-32" data-testid="dental-contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.95fr_1.05fr] gap-14 items-start">
          <div className="lg:sticky lg:top-32">
            <SectionHeading
              id="dental-cta"
              overline="Get Started"
              title={<>Fewer missed calls. Fewer forgotten follow-ups. <span className="text-brand-gradient">More support for your front desk.</span></>}
              sub="Let Medlyn handle the repetitive front-desk workload so your team can focus on the patients in front of them."
            />
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-2xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand-sky/10 border border-brand-sky/25 flex items-center justify-center shrink-0">
                  <Headset size={17} className="text-brand-navy dark:text-brand-sky" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Ask About Our Pilot Program</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
                    Prefer to start small? Mention the pilot in your message and we'll design a limited trial around one
                    workflow — like overflow calls or recall outreach.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8 md:p-10">
              <DentalLeadForm />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};
export default Dental;
